import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { DealCreateInterface } from '@zaoblako/shared/modules/business/dto/deal/deal.create.dto';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import { ProjectHelpdeskTasksKindEnum } from '@zaoblako/shared/modules/project/enum/project.helpdesk.tasks.kind.enum';
import { SubsKindEnum } from '@zaoblako/shared/modules/project/enum/subs.kind.enum';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealGroupsService } from '@zaoblako/web/core/services/deal/deal-groups.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { DealFormComponent } from '@zaoblako/web/modules/deal/dumbs/deal-form/deal-form.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { cloneDeep } from 'lodash';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'crm-deal-form-sidebar',
  standalone: true,
  templateUrl: './deal-form-sidebar.component.html',
  imports: [CommonModule, DealFormComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealFormSidebarComponent implements OnInit {
  @Input() public readonly index: number;

  public deal: DealCreateInterface = <DealCreateInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public groups: DealsGroupFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public companies: CompanyFindInterface[] = [];
  public tasks: HelpdeskTaskFindInterface[] = [];
  public projects: ProjectFindInterface[] = [];

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly dealService: DealService,
    private readonly companyService: CompanyService,
    private readonly dealGroupService: DealGroupsService,
    private readonly userGroupService: UserGroupService,
    private readonly directionService: DirectionService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly projectService: ProjectService,
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly clientService: ClientService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.dealGroupService.flat(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.directionService.find<DirectionFindInterface>()
    ]).subscribe(([groups, userGroups, directions]) => {
      this.groups = groups;
      this.userGroups = userGroups;
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    forkJoin([
      this.companyService.find<CompanyFindInterface>(),
      this.helpDeskTaskService.search(),
      this.projectService.search()
    ]).subscribe(([companies, result, projects]) => {
      this.companies = companies;
      this.tasks = result.items;
      this.projects = projects;
      this.cdRef.markForCheck();
    });

    const projectId = this.formStoreService.getState.forms[this.index]?.data?.projectId;
    const taskId = this.formStoreService.getState.forms[this.index]?.data?.taskId;
    const clientId = this.formStoreService.getState.forms[this.index]?.data?.clientId;

    if (!isNaN(projectId)) {
      this.projectService.findById<ProjectFindInterface>(projectId).subscribe((project) => {
        const projectFields: Array<keyof ProjectFindInterface> = [
          'name',
          'priority',
          'plannedMin',
          'direction',
          'unit',
          'filial',
          'userAuditors'
        ];
        projectFields.forEach((key) => {
          this.deal[key] = cloneDeep(project[key]);
        });
        this.deal.groupAuditors = project.userGroupAuditors;
        this.deal.projects = [project];
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(taskId)) {
      this.helpDeskTaskService.findById<HelpdeskTaskFindInterface>(taskId).subscribe((task) => {
        this.deal.helpdeskTasks = [task];
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(clientId)) {
      this.clientService.findById<ClientFindInterface>(clientId).subscribe((client) => {
        this.deal.contragent = client.contragents[0];
        this.deal.contragent.client = client;
        this.cdRef.markForCheck();
      });
    }

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    forkJoin([
      this.dealService.findById<DealCreateInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withProjects: true,
        withHelpdeskTasks: true
      }),
      this.dealService.getHelpdeskTask(this.formStoreService.getState.forms[this.index]?.entityId, {
        limit: 50,
        offset: 0,
        statusKind: ProjectHelpdeskTasksKindEnum.ALL
      }),
      this.dealService.getProjects(this.formStoreService.getState.forms[this.index]?.entityId, {
        limit: 50,
        offset: 0,
        statusKind: SubsKindEnum.ALL
      })
    ]).subscribe({
      next: ([deal, task, project]) => {
        this.deal = deal;
        this.deal.helpdeskTasks = task.items;
        this.deal.projects = project.items;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveDeal(deal: DealCreateInterface) {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      this.formStoreService.setLoading(true, this.index);

      this.dealService.update<DealCreateInterface, DealFindInterface>(deal).subscribe({
        next: (deal) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(`Сделка "${deal.name}" обновлена`);
          this.formStoreService.saveNext(deal, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.dealService.create<DealFindInterface>(deal).subscribe({
        next: (deal) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Сделка создана');
          if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
            this.formStoreService.closeSidebar(this.index);
            this.router.navigate(['/deal/card', deal.id]).catch(console.error);
          } else {
            this.formStoreService.saveNext(deal, this.index);
          }
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteDeal() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить сделку "${this.deal.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.dealService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`Сделка "${this.deal.name}" удалена`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }
}
